import React, { useEffect, useState, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { CheckCircle2, AlertTriangle, Wand2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Live GSM-7 encoding + segment meter for SMS template editors.
 *
 * Debounces the input, hits POST /api/templates/validate-sms, and
 * renders one of three states:
 *
 *   - GSM-7, N segments      → green, "you're good"
 *   - GSM-7, changed          → amber hint "we normalised smart quotes"
 *   - UCS-2, offenders listed → red, offers auto-fix (removes offenders)
 *                               and a manual "send as Unicode anyway" toggle
 *
 * `onNormalisedChange` is called only when the analyser found a safe
 * lookalike we can silently fix (em-dash → hyphen etc.) — the caller
 * chooses whether to swap the textarea contents.
 */
export const SmsHygieneMeter = ({ body, onNormalisedChange, allowUnicode, onAllowUnicodeChange }) => {
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const t = useRef(null);

  useEffect(() => {
    if (t.current) clearTimeout(t.current);
    if (!body) { setAnalysis(null); return; }
    setLoading(true);
    t.current = setTimeout(async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const res = await fetch(`${BACKEND_URL}/api/templates/validate-sms`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({ body }),
        });
        const j = await res.json();
        if (!res.ok) throw new Error(j.detail || 'validation failed');
        setAnalysis(j);
        setError('');
      } catch (e) { setError(e.message); } finally { setLoading(false); }
    }, 350);
    return () => t.current && clearTimeout(t.current);
  }, [body]);

  if (!analysis) return null;

  const gsm = analysis.is_gsm7;
  const bg = gsm ? '#ecfdf5' : '#fef2f2';
  const bd = gsm ? '#a7f3d0' : '#fecaca';
  const fg = gsm ? '#065f46' : '#991b1b';
  const Icon = gsm ? CheckCircle2 : AlertTriangle;

  return (
    <div
      data-testid="sms-hygiene-meter"
      style={{
        marginTop: 8,
        padding: '8px 12px',
        borderRadius: 8,
        background: bg,
        border: `1px solid ${bd}`,
        color: fg,
        fontSize: 12,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        flexWrap: 'wrap',
      }}
    >
      <Icon size={14} />
      <b>{analysis.encoding}</b>
      <span>
        {analysis.chars} char{analysis.chars === 1 ? '' : 's'}
        {analysis.encoding === 'GSM-7' && analysis.weighted_chars !== analysis.chars
          && ` (${analysis.weighted_chars} weighted)`}
        {' · '}
        {analysis.segments} segment{analysis.segments === 1 ? '' : 's'}
        {' · '}
        limit {analysis.max_per_segment}/seg
      </span>
      {analysis.changed && (
        <button
          type="button"
          className="btn s"
          onClick={() => onNormalisedChange && onNormalisedChange(analysis.cleaned)}
          data-testid="sms-hygiene-autofix"
          style={{ padding: '2px 8px', fontSize: 11 }}
        >
          <Wand2 size={11} style={{ verticalAlign: '-2px', marginRight: 4 }} />
          Auto-fix quotes/dashes
        </button>
      )}
      {analysis.offenders.length > 0 && (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          Non-GSM: <code style={{ background: '#fff', padding: '1px 4px', borderRadius: 4 }}>
            {analysis.offenders.slice(0, 8).join(' ')}
          </code>
          {analysis.offenders.length > 8 && `+${analysis.offenders.length - 8}`}
          {onAllowUnicodeChange && (
            <label style={{ display: 'inline-flex', gap: 4, alignItems: 'center', fontSize: 11 }}>
              <input
                type="checkbox"
                checked={!!allowUnicode}
                onChange={(e) => onAllowUnicodeChange(e.target.checked)}
                data-testid="sms-hygiene-allow-unicode"
              />
              Send as Unicode anyway (3× cost)
            </label>
          )}
        </span>
      )}
      {error && <span style={{ color: '#991b1b' }}>{error}</span>}
      {loading && <span style={{ opacity: 0.6 }}>…</span>}
    </div>
  );
};
